import { useEffect, useState } from 'react'
import {
  Paperclip,
  Download,
  Loader2,
  AlertCircle,
  Archive,
  ArchiveRestore,
  Mail,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import type { EmailDetail } from '@toga/shared'
import ArchiveEmailModal from './ArchiveEmailModal'

interface Props {
  emailId: string | null
  onArchived?: () => void
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function MessageReader({ emailId, onArchived }: Props) {
  const [email, setEmail] = useState<EmailDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [archiveOpen, setArchiveOpen] = useState(false)

  useEffect(() => {
    if (!emailId) {
      setEmail(null)
      return
    }
    setLoading(true)
    setError(null)
    api
      .get<{ email: EmailDetail }>(`/emails/${emailId}`)
      .then(({ email }) => setEmail(email))
      .catch((err: Error) => {
        if (err instanceof ApiError) {
          const body = err.body as { error?: string } | undefined
          setError(body?.error ?? err.message)
        } else {
          setError(err.message)
        }
      })
      .finally(() => setLoading(false))
  }, [emailId])

  const onUnarchive = async () => {
    if (!email) return
    if (!confirm('¿Desarchivar este correo?')) return
    try {
      await api.delete(`/emails/${email.id}/archive`)
      onArchived?.()
      // re-fetch
      const { email: updated } = await api.get<{ email: EmailDetail }>(`/emails/${email.id}`)
      setEmail(updated)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  if (!emailId) {
    return (
      <div className="h-full grid place-items-center t-muted text-sm">
        <div className="text-center">
          <Mail size={28} className="mx-auto t-faint mb-2" />
          Selecciona un correo a la izquierda.
        </div>
      </div>
    )
  }

  if (loading && !email) {
    return (
      <div className="p-8 t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando…
      </div>
    )
  }

  if (error || !email) {
    return (
      <div className="p-8 text-rose-300 flex items-center gap-2">
        <AlertCircle size={16} />
        {error ?? 'Correo no disponible'}
      </div>
    )
  }

  const isArchived = email.archivedToClientId || email.archivedToCaseId

  return (
    <div className="h-full overflow-y-auto">
      <header className="px-6 py-5 border-b border-base sticky top-0 backdrop-blur bg-topbar z-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold t-primary leading-tight">
              {email.subject || '(sin asunto)'}
            </h2>
            <div className="t-muted text-sm mt-1.5">
              <strong className="t-primary font-medium">{email.fromAddress}</strong>
              <span className="t-faint">
                {' '}
                · {new Date(email.receivedAt).toLocaleString('es-ES')}
              </span>
            </div>
            <div className="text-xs t-muted mt-1 space-y-0.5">
              {email.toAddresses.length > 0 && (
                <div>
                  <span className="t-faint">Para: </span>
                  {email.toAddresses.join(', ')}
                </div>
              )}
              {email.ccAddresses.length > 0 && (
                <div>
                  <span className="t-faint">CC: </span>
                  {email.ccAddresses.join(', ')}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {isArchived ? (
              <>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                  Archivado en {email.archivedToCaseCode ?? email.archivedToClientName ?? '—'}
                </span>
                <button
                  type="button"
                  onClick={onUnarchive}
                  className="px-3 py-1.5 rounded-lg text-xs bg-slate-800/60 hover:bg-slate-800 t-secondary flex items-center gap-1.5"
                >
                  <ArchiveRestore size={14} /> Desarchivar
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setArchiveOpen(true)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
              >
                <Archive size={14} /> Archivar en…
              </button>
            )}
            {email.bodyUrl && (
              <a
                href={email.bodyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-lg text-xs bg-slate-800/60 hover:bg-slate-800 t-secondary flex items-center gap-1.5"
              >
                <Download size={14} /> Descargar .eml
              </a>
            )}
          </div>
        </div>
      </header>

      <section className="px-6 py-5 space-y-4">
        <p className="t-secondary text-sm leading-relaxed whitespace-pre-wrap">{email.snippet}</p>
        <p className="text-xs t-faint italic">
          Mostrando vista previa. Para el cuerpo completo, descarga el .eml.
        </p>

        {email.attachments.length > 0 && (
          <div>
            <h3 className="text-xs uppercase tracking-wider t-faint mb-2 flex items-center gap-1.5">
              <Paperclip size={12} /> {email.attachments.length} adjunto
              {email.attachments.length === 1 ? '' : 's'}
            </h3>
            <ul className="space-y-1">
              {email.attachments.map((a) => (
                <li key={a.id} className="surface-soft px-3 py-2 text-sm flex items-center gap-3">
                  <Paperclip size={14} className="text-toga-300" />
                  <div className="flex-1 min-w-0">
                    <div className="t-primary truncate">{a.fileName}</div>
                    <div className="text-xs t-faint">
                      {a.mimeType} · {formatSize(a.sizeBytes)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {archiveOpen && (
        <ArchiveEmailModal
          emailId={email.id}
          subject={email.subject}
          onClose={() => setArchiveOpen(false)}
          onArchived={async () => {
            setArchiveOpen(false)
            onArchived?.()
            const { email: updated } = await api.get<{ email: EmailDetail }>(`/emails/${email.id}`)
            setEmail(updated)
          }}
        />
      )}
    </div>
  )
}
